import { useState, useCallback, useEffect, useRef } from 'react';
import { Routes, Route, useNavigate } from 'react-router-dom';
import DecisionFlow from '../components/prioritization-selector/DecisionFlow';
import BundleResults from '../components/prioritization-selector/BundleResults';
import BundleDetails from '../components/prioritization-selector/BundleDetails';
import MethodDetails from '../components/prioritization-selector/MethodDetails';
import type { SelectorState } from '../types/prioritization-selector';
import { createDecisionEngine } from '../utils/prioritization-selector/decisionEngine';
import { useToolSelectorStore } from '../stores/prioritization/toolSelectorStore';
import { getQuestionById } from '../data/prioritization-selector/decisionTree';
import type { ToolSelectorResponse } from '../types/prioritization/toolSelectorEvents';

function buildAuditResponses(responses: SelectorState['responses']): ToolSelectorResponse[] {
  return responses.map(r => {
    const question = getQuestionById(r.questionId);
    const option = question?.options?.find(o => o.value === r.value);
    return {
      questionId: r.questionId,
      questionText: question?.text ?? r.questionId,
      selectedValue: String(r.value),
      selectedText: option?.text ?? String(r.value),
    };
  });
}

export default function PrioritizationToolSelector() {
  const navigate = useNavigate();
  const [state, setState] = useState<SelectorState>({
    currentQuestion: 'Q1',
    responses: [],
    recommendation: null,
    isComplete: false,
    path: [],
    selectedScenario: null
  });

  // Tracks the persisted event ID so BundleResults can record the tool open
  const eventIdRef = useRef<string | null>(null);
  const recommendationLoggedRef = useRef(false);

  const logRecommendation = useToolSelectorStore(s => s.logRecommendation);

  // Persist the recommendation when the engine completes
  useEffect(() => {
    if (!state.isComplete || !state.recommendation || recommendationLoggedRef.current) return;
    recommendationLoggedRef.current = true;

    logRecommendation({
      responses: buildAuditResponses(state.responses),
      decisionPath: state.path,
      recommendedMethodIds: state.recommendation.recommendedMethods.map(m => m.id),
      recommendedMethodNames: state.recommendation.recommendedMethods.map(m => m.name),
      confidence: state.recommendation.confidence as 'high' | 'medium' | 'low' | null,
      reasoning: state.recommendation.reasoning ?? null,
      source: 'lab',
    }).then(id => {
      eventIdRef.current = id;
    });
  }, [state.isComplete, state.recommendation, state.responses, state.path, logRecommendation]);

  const updateResponse = useCallback((questionId: string, value: string | string[] | number) => {
    setState(prev => {
      const engine = createDecisionEngine(prev.responses);
      engine.addResponse(questionId, value);
      
      const newResponses = engine.getResponses();
      const currentQuestion = engine.getCurrentQuestion();
      const isComplete = engine.isComplete();
      const path = engine.getPath();
      const recommendation = isComplete ? engine.getRecommendation() : null;
      
      return {
        ...prev,
        responses: newResponses,
        currentQuestion,
        isComplete,
        path,
        recommendation
      };
    });
  }, []);

  const resetFlow = useCallback(() => {
    setState({
      currentQuestion: 'Q1',
      responses: [],
      recommendation: null,
      isComplete: false,
      path: [],
      selectedScenario: null
    });
    eventIdRef.current = null;
    recommendationLoggedRef.current = false;
    navigate('');
  }, [navigate]);

  const selectScenario = useCallback((scenarioId: string) => {
    setState(prev => ({
      ...prev,
      selectedScenario: scenarioId
    }));
  }, []);

  return (
    <div className="min-h-screen bg-[var(--surface)]">
      <main className="container mx-auto px-4 py-8">
        <Routes>
          <Route 
            path="/" 
            element={
              <DecisionFlow 
                state={state}
                onUpdateResponse={updateResponse}
                onSelectScenario={selectScenario}
              />
            } 
          />
          <Route 
            path="/results" 
            element={
              <BundleResults 
                recommendation={state.recommendation}
                onReset={resetFlow}
                eventIdRef={eventIdRef}
              />
            } 
          />
          <Route 
            path="/bundle/:id" 
            element={<BundleDetails />} 
          />
          <Route 
            path="/method/:id" 
            element={<MethodDetails />} 
          />
        </Routes>
      </main>
    </div>
  );
}
